import React, { useState } from 'react';
import { ArtistSchema } from '../../../types/schema';
import { ComponentProps, deps, inject, observer } from '../../../lib/component';
import { style, classes } from 'typestyle';
import { mediaDesktop } from '../utilities/mediaQueries';
import { FavouriteArtistButtonCompat } from '../../compat/favourite-artist-button-compat';
import { ResponsiveImage } from '../atoms/ResponsiveImage';
import { UtmLink } from '../atoms/UtmLink';
import { rebrand } from '../../../theme/color';
import { CircleButton } from '../atoms/button/CircleButton';
import NotifyButton from '../atoms/button/NotifyButton';
import { centerCenter, flexRoot, vertical } from 'csstips';
import { spacing } from '../../../theme/spacing';
import { px } from 'csx';
import { isEmpty } from '../utilities/objects';

interface ArtistProfileImageProps extends ComponentProps {
  artist: ArtistSchema;
  isLink?: boolean;
  className?: string;
}

const styles = {
  wrapper: style(
    {
      position: 'relative',
      width: '100%',
    },
    mediaDesktop({
      margin: '0 auto',
    })
  ),
  imageWrapper: style({
    display: 'block',
    position: 'relative',
    width: '100%',
    paddingBottom: '100%',
    overflow: 'hidden',
    borderRadius: '100%',
  }),
  nonActiveWrapper: style({
    $nest: {
      '&::after': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        backgroundColor: rebrand.dark1.toHexString(),
        opacity: 0.8,
      },
    },
  }),
  image: style({
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  }),
  notify: style(flexRoot, vertical, centerCenter, {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    padding: spacing.DEFAULT,
    zIndex: 1,
    color: rebrand.neutralOnDark[30].toHexString(),
    fontSize: px(12),
    textAlign: 'center',
  }),
  notifyText: style({
    position: 'absolute',
    top: 'calc(50% + 32px)',
    width: `calc(100% - ${spacing.XLARGE})`,
    left: 'auto',
    right: 'auto',
  }),
  favourite: style({
    position: 'absolute',
    top: 'calc(15% - 16px)',
    right: 'calc(15% - 16px)',
  }),
};

export const ArtistProfileImage: React.FC<ArtistProfileImageProps> = inject(deps)(
  observer(
    ({
      artist,
      isLink = false,
      className,
      model: {
        image: { cover },
        user: { user },
      },
      controller: { analytics, modal },
    }) => {
      const [notifiedForAvailability, setNotifiedForAvailability] = useState(false);

      if (!artist || !artist.images) return null;

      const { is_non_active_artist } = artist;

      const { identity } = artist.images;
      const imageSizes = [200, 400, 600];

      const imageSources = imageSizes.map((size) => {
        return cover(identity, {
          ignoreDimensions: true,
          width: size,
          height: size,
          crop: true,
        });
      });

      const ElementType = isLink && !is_non_active_artist ? UtmLink : 'div';

      const linkAttrs =
        isLink && !is_non_active_artist
          ? {
              href: `/music/artists/${artist.slug}`,
              'aria-label': artist.name,
            }
          : null;

      const handleNotifyClick = () => {
        analytics.sendMixpanel('User clicks notify button on non-active artist', {
          artist: artist.name,
        });

        if (isEmpty(user)) {
          modal.showCreateAccountPromptModal();
        } else {
          if (notifiedForAvailability) return;

          setNotifiedForAvailability(true);
        }
      };

      return (
        <div className={classes(styles.wrapper, className)}>
          <ElementType
            className={classes(styles.imageWrapper, is_non_active_artist && styles.nonActiveWrapper)}
            {...(isLink && linkAttrs)}
          >
            <ResponsiveImage
              className={styles.image}
              alt={artist.name}
              src={imageSources}
              width={imageSizes}
              height={imageSizes}
            />
          </ElementType>

          {is_non_active_artist ? (
            <div className={styles.notify}>
              <NotifyButton notifiedForAvailability={notifiedForAvailability} onClick={handleNotifyClick} />

              <span className={styles.notifyText}>
                {notifiedForAvailability ? "We'll notify you when available" : "We don't have this artist yet"}
              </span>
            </div>
          ) : (
            <div className={styles.favourite}>
              <FavouriteArtistButtonCompat artist={artist} />
            </div>
          )}
        </div>
      );
    }
  )
);
